import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
} from '@mui/icons-material';
import { ExportJob, JobsAPI } from '../services/jobs-api';
import { useJobs } from '../contexts/JobsContext';

interface JobCardProps {
  job: ExportJob;
}

export const JobCard: React.FC<JobCardProps> = ({ job }) => {
  const { cancelJob } = useJobs();
  const [expanded, setExpanded] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [showCancelDialog, setShowCancelDialog] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  // Update timer every second for running jobs
  React.useEffect(() => {
    if (job.status === 'running') {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [job.status]);

  const handleCancel = async () => {
    setShowCancelDialog(false);
    setCancelling(true);
    try {
      await cancelJob(job.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to cancel job';
      setErrorMessage(message);
    } finally {
      setCancelling(false);
    }
  };

  const getStatusColor = () => {
    switch (job.status) {
      case 'running':
        return 'primary';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (job.status) {
      case 'completed':
        return <CheckCircleIcon sx={{ fontSize: 18 }} />;
      case 'failed':
        return <ErrorIcon sx={{ fontSize: 18 }} />;
      case 'cancelled':
        return <CancelIcon sx={{ fontSize: 18 }} />;
      default:
        return null;
    }
  };

  const progress = JobsAPI.getProgressPercentage(job.progress);

  // Use live time for running jobs, otherwise use end time
  const endTimeForDuration = job.status === 'running' ? currentTime : job.endTime;
  const duration = JobsAPI.formatDuration(job.startTime, endTimeForDuration);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h6" component="div">
                {JobsAPI.getJobTypeName(job.type)}
              </Typography>
              <Chip
                label={job.status}
                color={getStatusColor()}
                size="small"
                icon={getStatusIcon() || undefined}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {job.sourceName} → {job.targetEnvironment}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {job.itemIds.length} items • {duration}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            {job.status === 'running' && (
              <Tooltip title="Cancel export">
                <IconButton
                  size="small"
                  onClick={() => setShowCancelDialog(true)}
                  disabled={cancelling}
                  color="error"
                >
                  <CancelIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={expanded ? "Show less" : "Show more"}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Progress Bar */}
        {job.status === 'running' && (
          <Box sx={{ mb: 2 }}>
            {/* Overall Job Progress */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Overall: {job.progress.current} of {job.progress.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} />

            {/* Show current status message even when overall progress is 0 */}
            {job.progress.message && (
              <Typography variant="caption" color="primary" sx={{ display: 'block', mt: 1 }}>
                {job.progress.message}
              </Typography>
            )}

            {/* Current Course Progress */}
            {job.progress.currentItem && job.progress.phase && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {job.progress.currentItem}: {job.progress.phase}
                  </Typography>
                  {/* Show phase progress percentage */}
                  {(() => {
                    let phaseProgress = 0;
                    const phase = job.progress.phase;

                    if (phase === 'Creating backup') {
                      phaseProgress = 33;
                    } else if (phase === 'Downloading backup' || phase === 'Downloading to local temp') {
                      // Download phase: 33% base + up to 33% based on bytes downloaded
                      const downloadPercent = job.progress.bytesToDownload
                        ? (job.progress.bytesDownloaded || 0) / job.progress.bytesToDownload * 33
                        : 0;
                      phaseProgress = 33 + downloadPercent;
                    } else if (phase === 'Uploading to S3' || phase === 'Ready to upload') {
                      // Upload phase: 66% base + up to 34% based on bytes uploaded
                      const uploadPercent = job.progress.bytesToUpload
                        ? (job.progress.bytesUploaded || 0) / job.progress.bytesToUpload * 34
                        : 0;
                      phaseProgress = 66 + uploadPercent;
                    }

                    return (
                      <Typography variant="caption" color="text.secondary">
                        {Math.round(phaseProgress)}%
                      </Typography>
                    );
                  })()}
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(() => {
                    let phaseProgress = 0;
                    const phase = job.progress.phase;

                    if (phase === 'Creating backup') {
                      phaseProgress = 33;
                    } else if (phase === 'Downloading backup' || phase === 'Downloading to local temp') {
                      const downloadPercent = job.progress.bytesToDownload
                        ? (job.progress.bytesDownloaded || 0) / job.progress.bytesToDownload * 33
                        : 0;
                      phaseProgress = 33 + downloadPercent;
                    } else if (phase === 'Uploading to S3' || phase === 'Ready to upload') {
                      const uploadPercent = job.progress.bytesToUpload
                        ? (job.progress.bytesUploaded || 0) / job.progress.bytesToUpload * 34
                        : 0;
                      phaseProgress = 66 + uploadPercent;
                    }

                    return Math.min(100, phaseProgress);
                  })()}
                  color="secondary"
                />
              </Box>
            )}

            {/* Byte Progress */}
            {(job.progress.bytesDownloaded !== undefined || job.progress.bytesUploaded !== undefined) && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1, justifyContent: 'center' }}>
                {job.progress.bytesDownloaded !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CloudDownloadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {JobsAPI.formatBytes(job.progress.bytesDownloaded)}
                      {job.progress.bytesToDownload && ` / ${JobsAPI.formatBytes(job.progress.bytesToDownload)}`}
                    </Typography>
                  </Box>
                )}
                {job.progress.bytesUploaded !== undefined && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CloudUploadIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {JobsAPI.formatBytes(job.progress.bytesUploaded)}
                      {job.progress.bytesToUpload && ` / ${JobsAPI.formatBytes(job.progress.bytesToUpload)}`}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Expanded Details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            {/* Job ID */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Job ID: {job.id}
            </Typography>

            {/* Result Details */}
            {job.result && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Results
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {job.result.message}
                </Typography>
                
                {job.result.exported.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="success.main">
                      ✓ Exported ({job.result.exported.length}):
                    </Typography>
                    <List dense>
                      {job.result.exported.slice(0, 5).map((file, idx) => (
                        <ListItem key={idx} sx={{ py: 0, pl: 2 }}>
                          <ListItemText
                            primary={file}
                            primaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                      {job.result.exported.length > 5 && (
                        <ListItem sx={{ py: 0, pl: 2 }}>
                          <ListItemText
                            primary={`... and ${job.result.exported.length - 5} more`}
                            primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                          />
                        </ListItem>
                      )}
                    </List>
                  </Box>
                )}

                {job.result.failed.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="error.main">
                      ✗ Failed ({job.result.failed.length}):
                    </Typography>
                    <List dense>
                      {job.result.failed.map((file, idx) => (
                        <ListItem key={idx} sx={{ py: 0, pl: 2 }}>
                          <ListItemText
                            primary={file}
                            primaryTypographyProps={{ variant: 'caption' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Box>
            )}

            {/* Job Logs */}
            {job.logs && job.logs.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Activity Log ({job.logs.length} entries)
                </Typography>
                <Box
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1
                  }}
                >
                  <List dense disablePadding>
                    {job.logs.map((log, idx) => {
                      const getLogColor = () => {
                        switch (log.level) {
                          case 'success': return 'success.main';
                          case 'error': return 'error.main';
                          case 'warning': return 'warning.main';
                          default: return 'text.secondary';
                        }
                      };

                      return (
                        <ListItem key={idx} sx={{ py: 0.5, px: 0, alignItems: 'flex-start' }}>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 60, flexShrink: 0 }}>
                                  {new Date(log.timestamp).toLocaleTimeString()}
                                </Typography>
                                <Typography variant="caption" color={getLogColor()} sx={{ fontFamily: 'monospace', flex: 1 }}>
                                  {log.message}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              </Box>
            )}

            {/* Error Details */}
            {job.error && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'error.main', color: 'error.contrastText', borderRadius: 1 }}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {job.error}
                </Typography>
              </Box>
            )}
          </Box>
        </Collapse>
      </CardContent>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel Export Job?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel this export?
            <br /><br />
            <strong>Job ID:</strong> {job.id}
            <br />
            <strong>Progress:</strong> {job.progress.current} of {job.progress.total} items
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelDialog(false)}>
            No, Keep Running
          </Button>
          <Button onClick={handleCancel} color="error" variant="contained">
            Yes, Cancel Job
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Card>
  );
};
